import { useState, useEffect, useCallback } from 'react';
import { syncNow, getPendingCount } from '@/lib/offlineSync';
import { toast } from 'sonner';

export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); autoSync(); };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check pending count periodically
  useEffect(() => {
    const check = () => getPendingCount().then(setPending);
    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, []);

  const autoSync = useCallback(async () => {
    const count = await getPendingCount();
    if (count === 0) return;
    setSyncing(true);
    const result = await syncNow();
    setSyncing(false);
    if (result.synced > 0) {
      toast.success(`Synced ${result.synced} offline change${result.synced > 1 ? 's' : ''}`);
    }
    setPending(await getPendingCount());
  }, []);

  if (isOnline && pending === 0) return null;

  return (
    <div data-testid="offline-indicator" style={{
      position: 'fixed', bottom: '20px', left: '20px', zIndex: 9999,
      background: isOnline ? '#D4AF37' : '#ef4444', color: isOnline ? '#000' : '#fff',
      padding: '10px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: '8px',
    }}>
      <span style={{
        width: '8px', height: '8px', borderRadius: '50%',
        background: isOnline ? '#000' : '#fff',
        animation: syncing ? 'pulse 1s infinite' : 'none',
      }} />
      {!isOnline && 'Offline — changes will sync when connected'}
      {isOnline && pending > 0 && (
        <>
          {syncing ? 'Syncing...' : `${pending} pending change${pending > 1 ? 's' : ''}`}
          {!syncing && (
            <button onClick={autoSync}
              style={{ marginLeft: '4px', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', font: 'inherit' }}>
              Sync Now
            </button>
          )}
        </>
      )}
    </div>
  );
}
